import React from "react";
import StickyFooter from "../../../../library/StickyFooter";
import PermissionsGate from "../../../../../utils/permissionGate";
import useVersionDetailsForm from "../hooks/useVersionDetailsForm";
import VersionSettingsPanel from "./VersionSettingsPanel";
import StatementOfResultsPanel from "./StatementOfResultsPanel";
import { StyledColumn, StyledFooterMessage, StyledLayout } from "./VersionDetailsForm.styles";

/**
 * Version Details form (SPEC2): Version Settings on the left, Statement of
 * Results + Student Portal settings on the right, saved via a sticky
 * Save/Cancel bar. Field state/validation/option-lists live in
 * `useVersionDetailsForm`; this component is layout only.
 * @param {object} props.versionData The version row (`getVersion`'s response), or `{}` on create.
 * @param {string | number} props.programId PMC programme id.
 * @param {string | number} [props.versionId] Version id from the route.
 * @param {boolean} props.loading Save-in-flight flag, and its setter.
 */
function VersionDetailsForm({ versionData, type, programId, versionId, loading, setLoading, setRefresh }) {
    const {
        formik,
        coordinatorOptions,
        isCoordinatorLoading,
        sorTemplateOptions,
        brandOptions,
        handleReset,
        hasEditAccess,
        isVersionLocked,
    } = useVersionDetailsForm({ versionData, type, programId, versionId, setLoading, setRefresh });

    const isEditing = type === "details" && Boolean(versionId);
    /*
     * "create" and "clone" are unsaved-by-nature - a clone's fields start pre-filled from
     * the source version (e.g. name already reads "V1 (Copy)"), so formik.dirty alone can
     * stay false until the person edits something, hiding Save/Cancel entirely and leaving
     * them with no way to actually save the duplicate.
     */
    const isUnsavedNew = type === "create" || type === "clone";
    const showStickyFooter = formik.dirty || isUnsavedNew;

    return (
        <form onSubmit={formik.handleSubmit}>
            <StyledLayout $reserveFooterSpace={showStickyFooter}>
                <StyledColumn $span={7}>
                    <VersionSettingsPanel
                        formik={formik}
                        coordinatorOptions={coordinatorOptions}
                        isCoordinatorLoading={isCoordinatorLoading}
                        isVersionLocked={isVersionLocked}
                        hasEditAccess={hasEditAccess}
                    />
                </StyledColumn>

                <StyledColumn $span={5}>
                    <StatementOfResultsPanel
                        formik={formik}
                        sorTemplateOptions={sorTemplateOptions}
                        brandOptions={brandOptions}
                        isVersionLocked={isVersionLocked}
                        hasEditAccess={hasEditAccess}
                    />
                </StyledColumn>
            </StyledLayout>

            {showStickyFooter && (
                <PermissionsGate
                    scopes={[isEditing ? "pmcversionedit" : "pmcversionadd"]}
                    errorProps={{ saveDisabled: true }}
                >
                    <StickyFooter onSave={formik.handleSubmit} onCancel={handleReset} isLoading={loading}>
                        <StyledFooterMessage>You have unsaved changes</StyledFooterMessage>
                    </StickyFooter>
                </PermissionsGate>
            )}
        </form>
    );
}

export default VersionDetailsForm;
